import React, { memo, useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import * as pdfjsLib from "pdfjs-dist";
import { getPdfjsOptions } from "../../../utils/pdfjsConfig";
import CustomLoading from "../../../Loading/CustomLoading";
import { preparePdfSource } from "../utils/pdfFetch";

export interface CustomPdfPreviewCanvasProps {
  pdfUrl: string;
  width?: number;
  height?: number;
  onClick?: () => void;
  onLoadComplete?: () => void;
  onError?: (error: Error) => void;
}

const CustomPdfPreviewCanvasComponent: React.FC<CustomPdfPreviewCanvasProps> = ({
  pdfUrl,
  width = 170,
  height = 115,
  onClick,
  onLoadComplete,
  onError,
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);
    const pdfRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);

    useEffect(() => {
      if (!pdfUrl || !canvasRef.current) return;

      let cancelled = false;

      const renderPdf = async () => {
        try {
          setLoading(true);
          setError(false);

          let pdfSource: string | Uint8Array;
          try {
            pdfSource = await preparePdfSource(pdfUrl);
            if (cancelled) return;
          } catch (fetchError) {
            if (cancelled) return;
            console.error("PDF取得エラー:", fetchError);
            setLoading(false);
            setError(true);
            onError?.(fetchError as Error);
            return;
          }

          const loadingTask = pdfjsLib.getDocument(
            typeof pdfSource === "string"
              ? { url: pdfSource, ...getPdfjsOptions() }
              : { data: pdfSource, ...getPdfjsOptions() }
          );

          const pdf = await loadingTask.promise;
          if (cancelled) {
            pdf.destroy();
            return;
          }

          pdfRef.current = pdf;
          const page = await pdf.getPage(1);

          const canvas = canvasRef.current;
          if (!canvas || cancelled) return;

          const context = canvas.getContext("2d");
          if (!context) return;

          const viewport = page.getViewport({ scale: 1 });
          const scale = Math.min(width / viewport.width, height / viewport.height);
          const scaledViewport = page.getViewport({ scale });

          const dpr = window.devicePixelRatio || 1;
          canvas.width = scaledViewport.width * dpr;
          canvas.height = scaledViewport.height * dpr;
          context.scale(dpr, dpr);

          const renderContext = {
            canvasContext: context,
            viewport: scaledViewport,
          } as Parameters<typeof page.render>[0];

          const renderTask = page.render(renderContext);
          renderTaskRef.current = renderTask;
          await renderTask.promise;

          if (cancelled) return;

          setLoading(false);
          onLoadComplete?.();
        } catch (err) {
          if (cancelled) return;
          console.error("PDF読み込みエラー:", err);
          setLoading(false);
          setError(true);
          onError?.(err as Error);
        }
      };

      renderPdf();

      return () => {
        cancelled = true;
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }
        if (pdfRef.current) {
          pdfRef.current.destroy();
          pdfRef.current = null;
        }
      };
    }, [pdfUrl, width, height, onLoadComplete, onError]);

    return (
      <Box
        onClick={onClick}
        sx={{
          width: "100%",
          height: height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          cursor: onClick ? "pointer" : "default",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              zIndex: 1,
            }}
          >
            <CustomLoading
              loading={loading}
              fullScreen={false}
              variant="circular"
              size="small"
              color="#0B9DBD"
              background="transparent"
            />
          </Box>
        )}
        {error ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M28 4H12C9.79 4 8 5.79 8 8v32c0 2.21 1.79 4 4 4h24c2.21 0 4-1.79 4-4V16L28 4z"
                fill="#e53935"
              />
              <path d="M28 4v12h12" fill="#ffcdd2" />
              <path
                d="M34 26v2H14v-2h20zm0 4v2H14v-2h20zm-10 4v2H14v-2h10z"
                fill="white"
              />
              <text
                x="24"
                y="22"
                textAnchor="middle"
                fill="white"
                fontSize="10"
                fontWeight="bold"
                fontFamily="Arial, sans-serif"
              >
                PDF
              </text>
            </svg>
            <span
              style={{
                fontSize: "11px",
                color: "#666",
                fontWeight: 500,
              }}
            >
              クリックして表示
            </span>
          </Box>
        ) : (
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: "100%",
              maxHeight: `${height}px`,
              objectFit: "contain",
              display: loading ? "none" : "block",
            }}
          />
        )}
      </Box>
    );
};

CustomPdfPreviewCanvasComponent.displayName = "CustomPdfPreviewCanvas";

const CustomPdfPreviewCanvas = memo(CustomPdfPreviewCanvasComponent);

export default CustomPdfPreviewCanvas;

